import React, { useState, useEffect } from 'react';
import './SignupPage.css';
import { Link, useNavigate } from 'react-router-dom';
import { getCurrentSession, signUpWithPassword, signInWithGoogle } from '../services/authService';
import { supabase } from '../services/supabaseClient';

const EMAIL_REGEX = /\S+@\S+\.\S+/;

function SignupPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();

  // ✅ Strong password validator
  const validatePassword = (password) => {
    const minLength = password.length >= 10;
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    return minLength && hasUpper && hasLower && hasNumber && hasSpecial;
  };

  useEffect(() => {
    const checkSession = async () => {
      const session = await getCurrentSession();
      if (session) {
        navigate('/dashboard');
      }
    };
    checkSession();
  }, [navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isSubmitting) return;

    setErrorMessage('');
    setSuccessMessage('');

    if (!EMAIL_REGEX.test(email)) {
      setEmailError('Please enter a valid email');
      return;
    }
    setEmailError('');

    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match!");
      return;
    }

    // ✅ Enforce strong password
    if (!validatePassword(password)) {
      setErrorMessage(
        "Password must be at least 10 characters and include uppercase, lowercase, number and special character."
      );
      return;
    }

    try {
      setIsSubmitting(true);
      setLoading(true);

      // Check if user already exists in profiles (public table check)
      // This helps avoid the "fake success" from Supabase if email confirmation is on
      const { data: existingUser } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', email)
        .maybeSingle();

      if (existingUser) {
        setErrorMessage("User already exists. Please login.");
        return;
      }

      // Proceed with signup if not found
      const { error } = await signUpWithPassword({ email, password });

      if (error) {
        if (
          error.message?.toLowerCase().includes("already registered") ||
          error.message?.toLowerCase().includes("already exists") ||
          error.message?.toLowerCase().includes("user already existent")
        ) {
          setErrorMessage("User already exists. Please login.");
        } else {
          setErrorMessage(error.message);
        }
        return;
      }

      setSuccessMessage('Success! Please check your email for a confirmation link.');
    } catch (err) {
      if (err.name === 'AbortError') return;

      console.log('Supabase signup error:', err);
      // Fallback catch, though explicit error checking above handles most
      setErrorMessage(err.message || "An unexpected error occurred during signup.");
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };


  const handleGoogleSignup = async () => {
    try {
      setLoading(true);
      const { error } = await signInWithGoogle();
      if (error) throw error;
    } catch (err) {
      setErrorMessage(err.message || 'Google signup failed');
      setLoading(false);
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (emailError) setEmailError('');
  };

  return (
    <div className="login-page-container">
      {/* Ambient Grid Background Layer from Home Page */}
      <div className="hero-background-grid" style={{ zIndex: 0 }} />
      <div className="hero-background-glow" style={{ zIndex: 0 }} />

      <Link to="/login" className="top-action-button" style={{ zIndex: 10 }}>Sign In</Link>

      <div className="login-header" style={{ zIndex: 10 }}>
        <div className="login-logo">Task Square (Beta)</div>
      </div>

      <div className="login-form-card">
        <h2>Create Account</h2>
        <p className="login-subtitle">Enter your details below</p>
        <form onSubmit={handleSubmit}>
          {errorMessage && <p className="error-message">{errorMessage}</p>}
          {successMessage && <p className="success-message">{successMessage}</p>}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={handleEmailChange}
              required
              autoComplete="email"
              spellCheck="false"
              className={emailError ? 'input-error bg-glass' : 'bg-glass'}
              disabled={loading}
            />
            {emailError && <p className="error-message inline-error">{emailError}</p>}
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
              spellCheck="false"
              className="bg-glass"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              autoComplete="new-password"
              spellCheck="false"
              className="bg-glass"
              disabled={loading}
            />
          </div>

          <button type="submit" className={`login-button ${loading ? 'loading' : ''}`} disabled={loading || isSubmitting}>
            {loading ? (
              <span className="spinner-loader"></span>
            ) : (
              'Sign Up'
            )}
          </button>
        </form>

        <div className="login-footer">
          <Link to="/login">Already have an account? Log In</Link>
        </div>

        <div className="divider-section">
          <div className="divider-line"></div>
          <span className="divider-text">or continue with</span>
          <div className="divider-line"></div>
        </div>

        <div className="social-login-container">
          <button type="button" className="social-button" onClick={handleGoogleSignup} disabled={loading}>
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            <span>Google</span>
          </button>



        </div>
      </div>
    </div>
  );
}

export default SignupPage;